import * as React from 'react';
import {
    BarController,
    BarElement,
    CategoryScale,
    Chart as ChartJS,
    type ChartData,
    Filler,
    Legend,
    LinearScale,
    LineController,
    LineElement,
    PointElement,
    TimeScale,
    Tooltip,
} from 'chart.js';
import 'chartjs-adapter-date-fns';
import { Chart } from 'react-chartjs-2';
import {
    DefaultButton,
    Dropdown,
    type IDropdownOption,
    MessageBar,
    MessageBarType,
    PrimaryButton,
    Spinner,
    SpinnerSize,
    VirtualizedComboBox,
} from '@fluentui/react';
import { useSearchParams } from 'react-router-dom';
import { convertToCSV, downloadCSV } from '../../utils/csvExport';
import { loadComputeFilterOptions, loadComputeInsight, loadComputeSourceStatus } from './computeApi';
import { defaultComputeFilters, readComputeFilters, writeComputeFilters } from './computeFilters';
import type {
    ComputeFilterOptions,
    ComputeFilters,
    ComputeInsightKind,
    ComputeInsightResponse,
    ComputeSeries,
    ComputeSourceStatus,
    ComputeSourceStatusResponse,
    ComputeTableColumn,
    ComputeValue,
} from './computeTypes';
import { formatFullNumber, formatReadableNumber } from './numberFormat';
import '../../styles/ComputeInsights.css';

ChartJS.register(
    BarController, BarElement, CategoryScale, LinearScale, LineController, TimeScale, PointElement, LineElement, Filler, Tooltip, Legend,
);

const PAGE_DETAILS: Record<ComputeInsightKind, { title: string; description: string }> = {
    'cores-per-kdau': {
        title: 'Cores/kDAU: Total & USR',
        description: 'Total and USR compute cores per thousand daily active users, segmented by customer type.',
    },
    'cores-per-kdau-bot': {
        title: 'Cores/kDAU: BOT',
        description: 'BOT compute cores per thousand daily active users, segmented by customer type.',
    },
    'active-users': {
        title: 'DAU & MAU',
        description: 'Daily and monthly active users driving SPO compute demand.',
    },
};

const SERIES_COLORS = ['#0f766e', '#d97706', '#2563eb', '#7c3aed', '#db2777', '#65a30d'];

export function ComputeInsightsPage({ kind }: { kind: ComputeInsightKind }) {
    const [searchParams, setSearchParams] = useSearchParams();
    const filters = React.useMemo(() => readComputeFilters(searchParams), [searchParams]);
    const [result, setResult] = React.useState<ComputeInsightResponse | null>(null);
    const [loading, setLoading] = React.useState(true);
    const [error, setError] = React.useState<string | null>(null);
    const [sourceStatus, setSourceStatus] = React.useState<ComputeSourceStatusResponse | null>(null);
    const [sourceStatusError, setSourceStatusError] = React.useState<string | null>(null);
    const [filterOptions, setFilterOptions] = React.useState<ComputeFilterOptions | null>(null);
    const invalidRange = filters.startDate > filters.endDate;

    React.useEffect(() => {
        document.title = `SPO Compute - ${PAGE_DETAILS[kind].title}`;
    }, [kind]);

    React.useEffect(() => {
        const controller = new AbortController();
        loadComputeFilterOptions(controller.signal)
            .then(setFilterOptions)
            .catch((requestError: unknown) => {
                if (!(requestError instanceof DOMException && requestError.name === 'AbortError')) {
                    setFilterOptions(null);
                }
            });
        loadComputeSourceStatus(controller.signal)
            .then(status => {
                setSourceStatus(status);
                setSourceStatusError(null);
            })
            .catch((requestError: unknown) => {
                if (requestError instanceof DOMException && requestError.name === 'AbortError') {
                    return;
                }
                setSourceStatus(null);
                setSourceStatusError(
                    requestError instanceof Error ? requestError.message : 'Unable to load SQL source status.',
                );
            });
        return () => controller.abort();
    }, []);

    React.useEffect(() => {
        if (invalidRange) {
            setResult(null);
            setError(null);
            setLoading(false);
            return;
        }

        const controller = new AbortController();
        setLoading(true);
        setError(null);

        loadComputeInsight(kind, filters, controller.signal)
            .then(data => {
                setResult(data);
                setLoading(false);
            })
            .catch((requestError: unknown) => {
                if (requestError instanceof DOMException && requestError.name === 'AbortError') {
                    return;
                }
                setResult(null);
                setError(requestError instanceof Error ? requestError.message : 'Unable to load SPO Compute Insights.');
                setLoading(false);
            });

        return () => controller.abort();
    }, [filters, invalidRange, kind]);

    const updateFilters = React.useCallback((updates: Partial<ComputeFilters>) => {
        const next = { ...filters, ...updates };
        setSearchParams(writeComputeFilters(searchParams, next), { replace: true });
    }, [filters, searchParams, setSearchParams]);

    const resetFilters = React.useCallback(() => {
        setSearchParams(writeComputeFilters(new URLSearchParams(), defaultComputeFilters()), { replace: true });
    }, [setSearchParams]);

    const details = PAGE_DETAILS[kind];

    return (
        <main className="compute-page page-container">
            <header className="compute-heading">
                <div>
                    <div className="compute-eyebrow">SPO Compute Insights</div>
                    <h1>{details.title}</h1>
                    <p>{result?.description || details.description}</p>
                </div>
                <div className="compute-heading-status">
                    <Freshness freshness={result?.freshness} loading={loading} />
                    <SourceStatus status={sourceStatus} error={sourceStatusError} />
                </div>
            </header>

            <FilterBar
                filters={filters}
                options={filterOptions}
                onChange={updateFilters}
                onReset={resetFilters}
            />

            {invalidRange && (
                <MessageBar messageBarType={MessageBarType.warning}>
                    Start date must be on or before end date.
                </MessageBar>
            )}
            {error && <MessageBar messageBarType={MessageBarType.error}>{error}</MessageBar>}
            {result?.freshness?.isStale && (
                <MessageBar messageBarType={MessageBarType.warning}>
                    This data is older than expected. Use caution when making compute decisions.
                </MessageBar>
            )}
            {result?.warnings?.map((warning, index) => (
                <MessageBar key={`${warning}-${index}`} messageBarType={MessageBarType.warning}>{warning}</MessageBar>
            ))}

            {loading ? (
                <div className="compute-loading"><Spinner size={SpinnerSize.large} label="Loading compute data..." /></div>
            ) : result && !invalidRange ? (
                <>
                    <SummaryCards metrics={result.summary} />
                    <section className="compute-panel">
                        <div className="compute-panel-heading">
                            <div>
                                <h2>Trend</h2>
                                <span>{filters.startDate} to {filters.endDate}</span>
                            </div>
                        </div>
                        <ComputeChart series={result.series} />
                    </section>
                    <section className="compute-panel">
                        <div className="compute-panel-heading">
                            <div>
                                <h2>Details</h2>
                                <span>{result.table.rows.length.toLocaleString()} rows</span>
                            </div>
                            <DefaultButton
                                text="Export CSV"
                                iconProps={{ iconName: 'Download' }}
                                disabled={result.table.rows.length === 0}
                                onClick={() => exportResult(kind, result)}
                            />
                        </div>
                        <ComputeTableView result={result} />
                    </section>
                </>
            ) : null}
        </main>
    );
}

function SourceStatus({
    status,
    error,
}: {
    status: ComputeSourceStatusResponse | null;
    error: string | null;
}) {
    if (error) {
        return <div className="compute-source-status-pill warning" title={error}>SQL status unavailable</div>;
    }
    if (!status) {
        return <div className="compute-source-status-pill">Checking SQL…</div>;
    }

    const sourceGroups = groupSourceStatuses(status.sources);
    const requiredGroups = sourceGroups.filter(source => source.isRequired);
    const migrationGroups = sourceGroups.filter(source => !source.isRequired);
    const healthyCount = requiredGroups.filter(source => source.isHealthy).length;
    const migrationTables = migrationGroups.flatMap(source => source.tables);
    const migrationReadyCount = migrationTables.filter(table => table.isHealthy).length;
    return (
        <details className={`compute-source-status ${healthyCount === requiredGroups.length ? '' : 'warning'}`}>
            <summary>
                SQL sources: {healthyCount}/{requiredGroups.length} healthy
                {migrationTables.length > 0 && ` · Migration: ${migrationReadyCount}/${migrationTables.length} ready`}
            </summary>
            <div className="compute-source-grid">
                <div className="compute-source-checked">
                    Checked {new Date(status.checkedAtUtc).toLocaleTimeString()}
                </div>
                {sourceGroups.map(source => (
                    <article key={source.key} className={source.isHealthy ? 'healthy' : source.isRequired ? 'unavailable' : 'pending'}>
                        <div>
                            <strong>{source.name}</strong>
                            <span>{source.server} / {source.database}</span>
                        </div>
                        <div className="compute-source-result">
                            <strong>{sourceResultLabel(source.isHealthy, source.isRequired)}</strong>
                        </div>
                        <div className="compute-source-tables">
                            {source.tables.map(table => (
                                <div key={table.key}>
                                    <code>{table.objectName}</code>
                                    <span className={table.isHealthy ? 'healthy' : source.isRequired ? 'unavailable' : 'pending'}>
                                        {table.isHealthy ? 'Healthy' : table.message}
                                    </span>
                                    <small>
                                        {table.dataAsOfUtc ? `Through ${formatDateOnly(table.dataAsOfUtc)} · ` : ''}
                                        {table.durationMilliseconds.toLocaleString()} ms
                                        {table.errorCode ? ` · ${table.errorCode}` : ''}
                                    </small>
                                </div>
                            ))}
                        </div>
                    </article>
                ))}
            </div>
        </details>
    );
}

function sourceResultLabel(isHealthy: boolean, isRequired: boolean): string {
    if (isHealthy) return isRequired ? 'Healthy' : 'Ready';
    return isRequired ? 'Unavailable' : 'Not ready';
}

function groupSourceStatuses(sources: ComputeSourceStatus[]) {
    const groups = new Map<string, {
        key: string;
        name: string;
        server: string;
        database: string;
        isHealthy: boolean;
        isRequired: boolean;
        tables: ComputeSourceStatus[];
    }>();
    for (const source of sources) {
        const key = `${source.server}|${source.database}`;
        const existing = groups.get(key);
        if (existing) {
            existing.tables.push(source);
            existing.isHealthy = existing.isHealthy && source.isHealthy;
            existing.isRequired = existing.isRequired || source.isRequired !== false;
            continue;
        }
        groups.set(key, {
            key,
            name: sourceName(source.server, source.database),
            server: source.server,
            database: source.database,
            isHealthy: source.isHealthy,
            isRequired: source.isRequired !== false,
            tables: [source],
        });
    }
    return [...groups.values()];
}

function sourceName(server: string, database: string): string {
    const normalizedServer = server.toLowerCase();
    if (normalizedServer.includes('spocogsforecasting')) return 'SPO COGS Forecasting';
    if (normalizedServer.includes('spocogssql')) return 'SPO COGS';
    if (normalizedServer.includes('sporeportsvip')) return 'SPO Reports';
    if (normalizedServer.includes('odspbi')) return 'BusinessInsights (migration)';
    return database;
}

export type FilterBarField = 'geo' | 'farm' | 'customerSegment' | 'sku' | 'granularity';

const ALL_FILTER_FIELDS: readonly FilterBarField[] = ['geo', 'farm', 'customerSegment', 'sku', 'granularity'];

// A dashboard-defined filter (for example Region) shown in the same row as the built-in filters.
// No selected values means all values, like Geo. An empty string is a real (Blank) value.
export interface FilterBarDimension {
    id: string;
    label: string;
    value: readonly string[];
    options: string[] | null;
    unavailable?: boolean;
    onChange: (values: string[]) => void;
}

export function FilterBar({
    filters,
    options,
    onChange,
    onReset,
    fields = ALL_FILTER_FIELDS,
    dimensions = [],
}: {
    filters: ComputeFilters;
    options: ComputeFilterOptions | null;
    onChange: (updates: Partial<ComputeFilters>) => void;
    onReset: () => void;
    fields?: readonly FilterBarField[];
    dimensions?: readonly FilterBarDimension[];
}) {
    const show = (field: FilterBarField) => fields.includes(field);
    return (
        <section className="compute-filter-bar" aria-label="Compute filters">
            <label>From<input type="date" value={filters.startDate} onChange={event => onChange({ startDate: event.target.value })} /></label>
            <label>To<input type="date" value={filters.endDate} onChange={event => onChange({ endDate: event.target.value })} /></label>
            {dimensions.map(dimension => <DimensionPicker key={dimension.id} dimension={dimension} />)}
            {show('geo') && (
                <FilterPicker
                    id="compute-geo"
                    label="Geo"
                    value={filters.geo}
                    options={options?.geos}
                    onChange={value => onChange({ geo: value })}
                />
            )}
            {show('farm') && (
                <FilterPicker
                    id="compute-farm"
                    label="Farm"
                    value={filters.farm}
                    options={options?.farmLabels}
                    onChange={value => onChange({ farm: value })}
                />
            )}
            {show('customerSegment') && (
                <FilterPicker
                    id="compute-segment"
                    label="Customer segment"
                    value={filters.customerSegment}
                    options={options?.customerSegments}
                    onChange={value => onChange({ customerSegment: value })}
                />
            )}
            {show('sku') && (
                <FilterPicker
                    id="compute-sku"
                    label="SKU"
                    value={filters.sku}
                    options={options?.skus}
                    onChange={value => onChange({ sku: value })}
                />
            )}
            {show('granularity') && (
                <label>Group by
                    <select value={filters.granularity} onChange={event => onChange({ granularity: event.target.value as ComputeFilters['granularity'] })}>
                        <option value="day">Day</option>
                        <option value="week">Week</option>
                        <option value="month">Month</option>
                    </select>
                </label>
            )}
            <PrimaryButton text="Reset" onClick={onReset} />
        </section>
    );
}

// Searchable because dashboard dimensions can have thousands of values (for example operation IDs).
function DimensionPicker({ dimension }: { dimension: FilterBarDimension }) {
    const selected = dimension.value;
    const comboOptions = React.useMemo(
        () => [...new Set([...(dimension.options ?? []), ...selected])]
            .map(value => ({ key: value, text: value === '' ? '(Blank)' : value })),
        [dimension.options, selected],
    );
    return (
        <div className="compute-filter-picker compute-dimension-picker">
            <VirtualizedComboBox
                label={dimension.label}
                multiSelect
                autoComplete="on"
                allowFreeform={false}
                selectedKey={[...selected]}
                options={comboOptions}
                placeholder={dimension.unavailable ? 'Options unavailable' : dimension.options === null ? 'Loading options…' : 'All'}
                // Stay enabled while values are selected, so they can be cleared even if options failed to load.
                disabled={dimension.options === null && selected.length === 0}
                onChange={(_event, option) => {
                    if (!option) return;
                    const next = new Set(selected);
                    if (option.selected) next.add(String(option.key));
                    else next.delete(String(option.key));
                    dimension.onChange([...next]);
                }}
                calloutProps={{ calloutMaxHeight: 320 }}
            />
        </div>
    );
}

function FilterPicker({
    id,
    label,
    value,
    options,
    onChange,
}: {
    id: string;
    label: string;
    value: string;
    options?: string[];
    onChange: (value: string) => void;
}) {
    const selectedKeys = React.useMemo(
        () => value.split(',').map(item => item.trim()).filter(Boolean),
        [value],
    );
    const normalizedOptions = React.useMemo(
        () => [...new Set([
            ...(options?.filter(option => option.trim()) ?? []),
            ...selectedKeys,
        ])],
        [options, selectedKeys],
    );
    const dropdownOptions = React.useMemo<IDropdownOption[]>(
        () => normalizedOptions.map(option => ({ key: option, text: option })),
        [normalizedOptions],
    );
    const updateSelection = React.useCallback((
        _event: React.FormEvent<HTMLDivElement>,
        option?: IDropdownOption,
    ) => {
        if (!option) return;
        const next = new Set(selectedKeys);
        if (option.selected) {
            next.add(String(option.key));
        } else {
            next.delete(String(option.key));
        }
        onChange([...next].join(','));
    }, [onChange, selectedKeys]);

    return (
        <div className="compute-filter-picker">
            <label id={`${id}-label`}>{label}</label>
            <Dropdown
                aria-labelledby={`${id}-label`}
                multiSelect
                selectedKeys={selectedKeys}
                options={dropdownOptions}
                placeholder={options ? 'All' : 'Loading options…'}
                disabled={!options}
                onChange={updateSelection}
                calloutProps={{ calloutMaxHeight: 320 }}
            />
        </div>
    );
}

export function Freshness({ freshness, loading }: { freshness?: ComputeInsightResponse['freshness']; loading: boolean }) {
    if (loading && !freshness) {
        return <div className="compute-freshness">Refreshing…</div>;
    }
    if (!freshness?.generatedAt) {
        return <div className="compute-freshness warning">Freshness unavailable</div>;
    }
    return (
        <div className={`compute-freshness ${freshness.isStale ? 'warning' : ''}`}>
            <strong>{freshness.isStale ? 'Data delayed' : 'Data current'}</strong>
            <span>Updated {new Date(freshness.generatedAt).toLocaleString()}</span>
            {freshness.dataThrough && <span>Through {formatDateOnly(freshness.dataThrough)}</span>}
        </div>
    );
}

export function SummaryCards({ metrics }: { metrics: ComputeInsightResponse['summary'] }) {
    if (!metrics.length) {
        return <div className="compute-empty">No summary metrics are available for these filters.</div>;
    }
    return (
        <section className="compute-summary" aria-label="Compute summary">
            {metrics.map(metric => (
                <article className={`compute-summary-card ${metric.status ?? 'neutral'}`} key={metric.key}>
                    <span>{metric.label}</span>
                    <strong title={typeof metric.value === 'number' ? formatFullNumber(metric.value) : undefined}>{formatMetric(metric.value, metric.unit)}</strong>
                    {metric.changePercent != null && (
                        <small className={metric.changePercent > 0 ? 'increase' : metric.changePercent < 0 ? 'decrease' : ''}>
                            {metric.changePercent > 0 ? '+' : ''}{metric.changePercent.toFixed(1)}%
                        </small>
                    )}
                    {metric.detail && <small>{metric.detail}</small>}
                </article>
            ))}
        </section>
    );
}

// Line, stacked-area, and stacked-column charts. With bars, the bars stack on the left axis and the series are lines on a
// right axis, like a Power BI line and stacked column chart. Number x values (such as CPU %) use an evenly spaced category axis.
export function ComputeChart({ series, bars = [], type = 'line', xAxis = 'time' }: {
    series: ComputeSeries[];
    bars?: ComputeSeries[];
    type?: 'line' | 'area' | 'bar';
    xAxis?: 'time' | 'category';
}) {
    const all = [...bars, ...series];
    if (all.every(item => !item.points.length)) {
        return <div className="compute-empty">No trend data is available for these filters.</div>;
    }
    const combo = bars.length > 0;
    const stacked = type !== 'line' || combo;
    const colorOf = (item: ComputeSeries, index: number) => item.color || SERIES_COLORS[index % SERIES_COLORS.length];
    const labelOf = (item: ComputeSeries) => item.unit ? `${item.label} (${item.unit})` : item.label;
    const pointsOf = (item: ComputeSeries) => item.points.map(point => ({ x: point.timestamp, y: point.value }));
    const data: ChartData<'bar' | 'line', { x: string; y: number }[]> = {
        labels: xAxis === 'category'
            ? [...new Set(all.flatMap(item => item.points.map(point => point.timestamp)))].sort((left, right) => Number(left) - Number(right))
            : undefined,
        datasets: [
            ...bars.map((item, index) => ({
                type: 'bar' as const, label: labelOf(item), data: pointsOf(item), backgroundColor: colorOf(item, index),
                stack: 'bars', yAxisID: 'y', order: 2,
            })),
            ...series.map((item, index) => {
                const color = colorOf(item, bars.length + index);
                return type === 'bar'
                    ? { type: 'bar' as const, label: labelOf(item), data: pointsOf(item), backgroundColor: color, stack: 'series', yAxisID: 'y' }
                    : {
                        type: 'line' as const, label: labelOf(item), data: pointsOf(item), borderColor: color,
                        backgroundColor: type === 'area' ? `${color}66` : color,
                        fill: type === 'area' ? (index === 0 ? 'origin' : '-1') : false,
                        pointRadius: 1.5, pointHoverRadius: 5, borderWidth: 2, tension: 0.18,
                        yAxisID: combo ? 'y1' : 'y', order: 1,
                    };
            }),
        ],
    };
    const ticks = { callback: (value: string | number) => formatReadableNumber(Number(value)) };
    return (
        <div className="compute-chart">
            <Chart
                type="line"
                data={data}
                options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    // Rows can be missing for some series, so match points by x value rather than array index.
                    interaction: { mode: 'x', intersect: false },
                    scales: {
                        x: xAxis === 'time'
                            ? { type: 'time', time: { tooltipFormat: 'PP' }, grid: { display: false }, offset: type === 'bar' || combo }
                            : { type: 'category', grid: { display: false } },
                        y: { stacked, beginAtZero: stacked, ticks },
                        ...(combo ? { y1: { position: 'right' as const, grid: { drawOnChartArea: false }, ticks } } : {}),
                    },
                    plugins: {
                        legend: { position: 'bottom', align: 'start' },
                        tooltip: {
                            callbacks: {
                                label: context => `${context.dataset.label}: ${formatReadableNumber(context.parsed.y)}`,
                            },
                        },
                    },
                }}
            />
        </div>
    );
}

export function ComputeTableView({ result }: { result: ComputeInsightResponse }) {
    const { columns, rows } = result.table;
    if (!columns.length || !rows.length) {
        return <div className="compute-empty">No detail rows are available for these filters.</div>;
    }
    return (
        <div className="compute-table-wrap">
            <table className="compute-table">
                <thead><tr>{columns.map(column => <th key={column.key}>{column.label}</th>)}</tr></thead>
                <tbody>
                    {rows.map((row, rowIndex) => (
                        <tr key={rowIndex}>
                            {columns.map(column => {
                                const value = row[column.key];
                                return (
                                    <td
                                        key={column.key}
                                        title={column.format === 'number' && typeof value === 'number' ? formatFullNumber(value) : undefined}
                                    >
                                        {formatCell(value, column)}
                                    </td>
                                );
                            })}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

function formatMetric(value: number | string, unit?: string | null): string {
    const formatted = typeof value === 'number' ? formatReadableNumber(value) : value;
    if (!unit) return formatted;
    if (unit === '%') return `${formatted}%`;
    return `${formatted} ${unit}`;
}

function formatCell(value: ComputeValue | undefined, column: ComputeTableColumn): string {
    if (value === null || value === undefined) return '—';
    if (column.format === 'number' && typeof value === 'number') return formatReadableNumber(value);
    if (column.format === 'percent' && typeof value === 'number') return `${value.toFixed(1)}%`;
    if ((column.format === 'date' || column.format === 'datetime') && typeof value === 'string') {
        if (column.format === 'date') {
            return formatDateOnly(value);
        }
        const date = new Date(value);
        if (!Number.isNaN(date.getTime())) {
            return date.toLocaleString();
        }
    }

    return String(value);
}

function formatDateOnly(value: string): string {
    const datePart = value.slice(0, 10);
    const [year, month, day] = datePart.split('-').map(Number);
    if (!year || !month || !day) {
        return value;
    }
    return new Date(year, month - 1, day).toLocaleDateString();
}

export function exportResult(kind: string, result: ComputeInsightResponse) {
    const rows = result.table.rows.map(row => Object.fromEntries(
        result.table.columns.map(column => [column.label, row[column.key]]),
    ));
    const date = new Date().toISOString().slice(0, 10);
    downloadCSV(convertToCSV({ rows }), `spo-compute-${kind}-${date}.csv`);
}
